/**
 * @fileoverview Resolve a job CRM contact from an agent-source row.
 *
 * Creditor / employee / security-interest rows are projected into
 * `jobContacts.sourceLinks`. Compound names use a `#`-suffixed sourceId, so
 * matching strips that suffix (same rule as `sourceRowIdFromLinkId` on the
 * backend).
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { ContactSourceKind } from "@/lib/contact-roles";

export interface SourceLinkedContact {
  _id: Id<"jobContacts">;
  displayName: string;
  status: string;
  sourceLinks: ReadonlyArray<{
    kind: ContactSourceKind | string;
    sourceId: string;
  }>;
}

/**
 * Recover the agent-table row id from a source link. Most sourceIds ARE the
 * row id; joint / compound splits append `#…`.
 */
function sourceRowId(sourceId: string): string {
  const hash = sourceId.indexOf("#");
  return hash === -1 ? sourceId : sourceId.slice(0, hash);
}

/**
 * Active job contact whose source links point at this agent row.
 *
 * When several contacts share a compound source row, prefer an exact
 * sourceId match, then a display-name match, then the first active match.
 *
 * @param contacts - Job contacts to inspect.
 * @param kind - Source-link kind for the agent row.
 * @param sourceId - Agent-table row identifier (compound suffix stripped).
 * @param displayName - Optional name used to disambiguate compound rows.
 * @returns The matching job contact ID, or `null`.
 *
 * @example
 * findContactForSource(contacts, "liability", liabilityEntryId, creditorName);
 */
export function findContactForSource(
  contacts: ReadonlyArray<SourceLinkedContact>,
  kind: ContactSourceKind,
  sourceId: string,
  displayName?: string,
): Id<"jobContacts"> | null {
  const matches = contacts.filter((contact) => {
    if (contact.status !== "active") return false;
    return contact.sourceLinks.some(
      (link) => link.kind === kind && sourceRowId(link.sourceId) === sourceId,
    );
  });

  const [first] = matches;
  if (!first) return null;
  if (matches.length === 1) return first._id;

  const exact = matches.find((contact) =>
    contact.sourceLinks.some(
      (link) => link.kind === kind && link.sourceId === sourceId,
    ),
  );
  if (exact) return exact._id;

  if (displayName) {
    const byName = matches.find(
      (contact) =>
        contact.displayName.localeCompare(displayName, "en", {
          sensitivity: "accent",
        }) === 0,
    );
    if (byName) return byName._id;
  }

  return first._id;
}
